// Application state store
import { create } from 'zustand';
import { 
  Terminal, Checkpoint, Lane, LaneType, Section, SectionUnit,
  StatusChangeLog, DashboardStats, AppState 
} from '../types';
import { apiService } from '../services/api';

interface AppStore extends AppState {
  // Cache tracking
  lastFetch: {
    terminals?: number;
    sections?: number;
    sectionUnits?: number;
    checkpoints?: number;
    lanes?: number;
    laneTypes?: number;
    dashboardStats?: number;
    statusChanges?: number;
  };
  
  // Cache methods
  CACHE_TIMEOUT: number;
  isCacheValid: (key: keyof AppStore['lastFetch']) => boolean;
  
  // Actions with caching
  fetchTerminals: (forceRefresh?: boolean) => Promise<void>;
  fetchSections: (terminalId?: number, forceRefresh?: boolean) => Promise<void>;
  fetchSectionUnits: (sectionId?: number, forceRefresh?: boolean) => Promise<void>;
  fetchCheckpoints: (terminalId?: number, forceRefresh?: boolean) => Promise<void>;
  fetchLanes: (checkpointId?: number, forceRefresh?: boolean) => Promise<void>;
  fetchLaneTypes: (checkpointId?: number, forceRefresh?: boolean) => Promise<void>;
  fetchDashboardStats: (forceRefresh?: boolean) => Promise<void>;
  fetchStatusChanges: (entityType?: string, entityId?: number, forceRefresh?: boolean) => Promise<void>;
  
  // Setters
  setLoading: (key: string, loading: boolean) => void;
  setError: (key: string, error: string) => void;
  clearError: (key: string) => void;
  clearErrors: () => void;
  
  // Add/Update/Delete
  addTerminal: (terminal: Terminal) => void;
  updateTerminal: (terminal: Terminal) => void;
  removeTerminal: (terminalId: number) => void;
  
  addSection: (section: Section) => void;
  updateSection: (section: Section) => void;
  removeSection: (sectionId: number) => void;
  
  addSectionUnit: (sectionUnit: SectionUnit) => void;
  updateSectionUnit: (sectionUnit: SectionUnit) => void;
  removeSectionUnit: (sectionUnitId: number) => void;
  
  addCheckpoint: (checkpoint: Checkpoint) => void;
  updateCheckpoint: (checkpoint: Checkpoint) => void;
  
  addLane: (lane: Lane) => void;
  updateLane: (lane: Lane) => void;
  
  addLaneType: (laneType: LaneType) => void;
  updateLaneType: (laneType: LaneType) => void;
}

export const useAppStore = create<AppStore>((set, get) => ({
  // Initial state
  terminals: [],
  sections: [],
  sectionUnits: [],
  checkpoints: [],
  lanes: [],
  laneTypes: [],
  statusChanges: [],
  dashboardStats: null,
  loading: {},
  errors: {},
  lastFetch: {},

  // Cache timeout in milliseconds (5 minutes)
  CACHE_TIMEOUT: 5 * 60 * 1000,

  // Helper function to check if cache is valid
  isCacheValid: (key: keyof AppStore['lastFetch']): boolean => {
    const lastFetch = get().lastFetch[key];
    const cacheTimeout = (get() as any).CACHE_TIMEOUT;
    return lastFetch ? (Date.now() - lastFetch) < cacheTimeout : false;
  },

  // Actions
  fetchTerminals: async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh && (get() as any).isCacheValid('terminals') && get().terminals.length > 0) {
      return;
    }

    set({ loading: { ...get().loading, terminals: true } });
    try {
      const terminals = await apiService.getTerminals();
      const errors = { ...get().errors };
      delete errors.terminals;
      set({ 
        terminals,
        loading: { ...get().loading, terminals: false },
        errors,
        lastFetch: { ...get().lastFetch, terminals: Date.now() }
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, terminals: false },
        errors: { ...get().errors, terminals: error instanceof Error ? error.message : 'Failed to fetch terminals' }
      });
    }
  },

  fetchSections: async (terminalId?: number, forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh && (get() as any).isCacheValid('sections') && get().sections.length > 0) {
      return;
    }

    set({ loading: { ...get().loading, sections: true } });
    try {
      const sections = await apiService.getSections(terminalId);
      const errors = { ...get().errors };
      delete errors.sections;
      set({ 
        sections,
        loading: { ...get().loading, sections: false },
        errors,
        lastFetch: { ...get().lastFetch, sections: Date.now() }
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, sections: false },
        errors: { ...get().errors, sections: error instanceof Error ? error.message : 'Failed to fetch sections' }
      });
    }
  },

  fetchSectionUnits: async (sectionId?: number) => {
    set({ loading: { ...get().loading, sectionUnits: true } });
    try {
      const sectionUnits = await apiService.getSectionUnits(sectionId);
      const errors = { ...get().errors };
      delete errors.sectionUnits;
      set({ 
        sectionUnits,
        loading: { ...get().loading, sectionUnits: false },
        errors
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, sectionUnits: false },
        errors: { ...get().errors, sectionUnits: error instanceof Error ? error.message : 'Failed to fetch section units' }
      });
    }
  },

  fetchCheckpoints: async (terminalId?: number, forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh && (get() as any).isCacheValid('checkpoints') && get().checkpoints.length > 0) {
      return;
    }

    set({ loading: { ...get().loading, checkpoints: true } });
    try {
      const checkpoints = await apiService.getCheckpoints(terminalId);
      const errors = { ...get().errors };
      delete errors.checkpoints;
      set({ 
        checkpoints,
        loading: { ...get().loading, checkpoints: false },
        errors,
        lastFetch: { ...get().lastFetch, checkpoints: Date.now() }
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, checkpoints: false },
        errors: { ...get().errors, checkpoints: error instanceof Error ? error.message : 'Failed to fetch checkpoints' }
      });
    }
  },

  fetchLanes: async (checkpointId?: number) => {
    set({ loading: { ...get().loading, lanes: true } });
    try {
      const lanes = await apiService.getLanes(checkpointId);
      const errors = { ...get().errors };
      delete errors.lanes;
      set({ 
        lanes,
        loading: { ...get().loading, lanes: false },
        errors
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, lanes: false },
        errors: { ...get().errors, lanes: error instanceof Error ? error.message : 'Failed to fetch lanes' }
      });
    }
  },

  fetchLaneTypes: async (checkpointId?: number, forceRefresh = false) => {
    const cacheKey = checkpointId ? `laneTypes_${checkpointId}` : 'laneTypes';
    
    // Check cache first
    if (!forceRefresh && (get() as any).isCacheValid('laneTypes') && get().laneTypes.length > 0) {
      return;
    }

    set({ loading: { ...get().loading, laneTypes: true } });
    try {
      const laneTypes = await apiService.getLaneTypes(checkpointId);
      const errors = { ...get().errors };
      delete errors.laneTypes;
      set({ 
        laneTypes,
        loading: { ...get().loading, laneTypes: false },
        errors,
        lastFetch: { ...get().lastFetch, laneTypes: Date.now() }
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, laneTypes: false },
        errors: { ...get().errors, laneTypes: error instanceof Error ? error.message : 'Failed to fetch lane types' }
      });
    }
  },

  fetchDashboardStats: async () => {
    set({ loading: { ...get().loading, dashboard: true } });
    try {
      const dashboardStats = await apiService.getDashboard();
      const errors = { ...get().errors };
      delete errors.dashboard;
      set({ 
        dashboardStats,
        loading: { ...get().loading, dashboard: false },
        errors
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, dashboard: false },
        errors: { ...get().errors, dashboard: error instanceof Error ? error.message : 'Failed to fetch dashboard stats' }
      });
    }
  },

  fetchStatusChanges: async (entityType?: string, entityId?: number) => {
    set({ loading: { ...get().loading, statusChanges: true } });
    try {
      const statusChanges = await apiService.getStatusChanges(entityType, entityId);
      const errors = { ...get().errors };
      delete errors.statusChanges;
      set({ 
        statusChanges,
        loading: { ...get().loading, statusChanges: false },
        errors
      });
    } catch (error) {
      set({ 
        loading: { ...get().loading, statusChanges: false },
        errors: { ...get().errors, statusChanges: error instanceof Error ? error.message : 'Failed to fetch status changes' }
      });
    }
  },

  // Utility functions
  setLoading: (key: string, loading: boolean) => {
    set({ loading: { ...get().loading, [key]: loading } });
  },

  setError: (key: string, error: string) => {
    set({ errors: { ...get().errors, [key]: error } });
  },

  clearError: (key: string) => {
    const errors = { ...get().errors };
    delete errors[key];
    set({ errors });
  },

  clearErrors: () => {
    set({ errors: {} });
  },

  // Add/Update/Delete functions
  addTerminal: (terminal: Terminal) => {
    set({ terminals: [...get().terminals, terminal] });
  },

  updateTerminal: (updatedTerminal: Terminal) => {
    set({ 
      terminals: get().terminals.map(t => 
        t.terminal_id === updatedTerminal.terminal_id ? updatedTerminal : t
      )
    });
  },

  removeTerminal: (terminalId: number) => {
    set({ 
      terminals: get().terminals.filter(t => t.terminal_id !== terminalId)
    });
  },

  addSection: (section: Section) => {
    set({ sections: [...get().sections, section] });
  },

  updateSection: (updatedSection: Section) => {
    set({ 
      sections: get().sections.map(s => 
        s.section_id === updatedSection.section_id ? updatedSection : s
      )
    });
  },

  removeSection: (sectionId: number) => {
    set({ 
      sections: get().sections.filter(s => s.section_id !== sectionId)
    });
  },

  addSectionUnit: (sectionUnit: SectionUnit) => {
    set({ sectionUnits: [...get().sectionUnits, sectionUnit] });
  },

  updateSectionUnit: (updatedSectionUnit: SectionUnit) => {
    set({ 
      sectionUnits: get().sectionUnits.map(su => 
        su.section_unit_id === updatedSectionUnit.section_unit_id ? updatedSectionUnit : su
      )
    });
  },

  removeSectionUnit: (sectionUnitId: number) => {
    set({ 
      sectionUnits: get().sectionUnits.filter(su => su.section_unit_id !== sectionUnitId)
    });
  },

  addCheckpoint: (checkpoint: Checkpoint) => {
    set({ checkpoints: [...get().checkpoints, checkpoint] });
  },

  updateCheckpoint: (updatedCheckpoint: Checkpoint) => {
    set({ 
      checkpoints: get().checkpoints.map(c => 
        c.checkpoint_id === updatedCheckpoint.checkpoint_id ? updatedCheckpoint : c
      )
    });
  },

  addLane: (lane: Lane) => {
    set({ lanes: [...get().lanes, lane] });
  },

  updateLane: (updatedLane: Lane) => {
    set({ 
      lanes: get().lanes.map(l => 
        l.lane_id === updatedLane.lane_id ? updatedLane : l
      )
    });
  },

  addLaneType: (laneType: LaneType) => {
    set({ laneTypes: [...get().laneTypes, laneType] });
  },

  updateLaneType: (updatedLaneType: LaneType) => {
    set({ 
      laneTypes: get().laneTypes.map(lt => 
        lt.lane_type_id === updatedLaneType.lane_type_id ? updatedLaneType : lt
      )
    });
  }
}));
